import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api from "@/api";
import AdminCreators from "@/pages/admin/AdminCreators";
import AdminVideos from "@/pages/admin/AdminVideos";
import AdminPackages from "@/pages/admin/AdminPackages";
import AdminHome from "@/pages/admin/AdminHome";
import AdminInquiries from "@/pages/admin/AdminInquiries";

const TABS = [
  ["creators", "Creators"],
  ["videos", "Videos"],
  ["packages", "Packages"],
  ["home", "Homepage & Status"],
  ["inquiries", "Inquiries"],
] as const;

type Tab = (typeof TABS)[number][0];

export default function AdminPage() {
  const nav = useNavigate();
  const [me, setMe] = useState<null | false | { email: string }>(null);
  const [tab, setTab] = useState<Tab>("creators");

  useEffect(() => {
    api.get("/auth/me").then((r) => setMe(r.data.user)).catch(() => setMe(false));
  }, []);

  if (me === null) {
    return <div className="flex min-h-screen items-center justify-center bg-stone-100 text-sm text-stone-500">Loading…</div>;
  }
  if (me === false) return <Navigate to="/admin/login" replace />;

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    nav("/admin/login");
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-stone-100">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-4 py-4">
          <div>
            <p className="font-spacemono text-[10px] font-bold tracking-widest text-stone-500">SYS_ADMIN.EXE</p>
            <h1 className="text-lg font-bold text-stone-900">Late Nights Media — Admin</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden text-xs text-stone-500 sm:inline">{me.email}</span>
            <button data-testid="admin-view-site" onClick={() => nav("/")} className="border border-stone-300 px-3 py-1.5 text-xs font-semibold hover:bg-stone-50">
              View site
            </button>
            <button data-testid="admin-logout" onClick={logout} className="bg-stone-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-stone-700">
              Log out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-4 py-6">
        <div className="flex flex-wrap gap-1 border-b border-stone-200">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-3 py-2 text-xs font-semibold ${
                tab === key ? "border border-b-0 border-stone-200 bg-white text-stone-900" : "text-stone-500 hover:text-stone-900"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="border border-t-0 border-stone-200 bg-white p-4 sm:p-6">
          {tab === "creators" && <AdminCreators />}
          {tab === "videos" && <AdminVideos />}
          {tab === "packages" && <AdminPackages />}
          {tab === "home" && <AdminHome />}
          {tab === "inquiries" && <AdminInquiries />}
        </div>
      </div>
    </div>
  );
}
